<template>
  <div class="product-description">
    <div
      v-if="trustpilotWidget"
      class="product-description__reviews"
      v-html="trustpilotWidget"
    />

    <div class="product-description__usps">
      <div
        v-for="(usp, index) of usps"
        :key="`${usp.handle}-${index}`"
        class="product-description__usp text-center"
        :class="{ 'product-description__usp--span': oddUsps && index + 1 === usps.length }"
      >
        <div
          v-if="usp.icon && usp.icon !== 'none'"
          class="product-description__usp-icon"
        >
          <component :is="getUspIcon(usp.icon)" />
        </div>

        <p
          class="product-description__usp-title text-body-xs"
          v-text="usp.title"
        />

        <p
          v-if="usp.subtitle"
          class="product-description__usp-subtitle text-body-2xs"
          v-text="usp.subtitle"
        />
      </div>

      <next-day-shipping
        v-if="nextDayDeliverySettings.enable"
        class="product-description__next-day-shipping"
      />
    </div>

    <dynamic-accordion
      class="product-description__accordions"
      heading-type="h2"
      :panel-data="panelData"
    />

    <div class="product-description__support">
      <h2
        class="product-description__support-title text-body-subtitle-xs"
        v-text="$string('product.support.title')"
      />

      <span
        class="product-description__support-content text-body-xs"
        v-html="supportContent"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product description (product-description)
 * -----------------------------------------------------------------------------
 * Product USPs and description accordions.
 *
 * @param {Object} product - Liquid local product object.
 * @param {String} trustpilotWidget - Trustpilot quote reviews.
 * @param {Array} usps - Section block USPs.
 *
 */
import { mapState } from 'vuex'

import DynamicAccordion from '~async/dynamic-accordion/dynamic-accordion'
import NextDayShipping from '~async/next-day-shipping/next-day-shipping'

import IconDelivery from '~icons/misc/delivery.svg'
import IconOnesie from '~icons/misc/onesie.svg'
import IconPadlock from '~icons/misc/padlock.svg'
import IconReturns from '~icons/misc/returns.svg'

export default {
  name: 'ProductDescription',

  components: {
    DynamicAccordion,
    IconDelivery,
    IconOnesie,
    IconPadlock,
    IconReturns,
    NextDayShipping,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: () => ({
        description: '',
      }),
      required: true,
    },
    trustpilotWidget: {
      type: String,
      default: '',
    },
    usps: {
      type: Array,
      default: () => [],
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      nextDayDeliverySettings: (state) => state['next-day-shipping'].settings,
    }),

    /**
     * Compute if there is an odd number of USPs.
     * @return {Boolean}
     */
    oddUsps() {
      return this.usps.length && this.usps.length % 2
    },

    /**
     * Compute panel data for dynamic accordions.
     * @returns {Array}
     */
    panelData() {
      const productDescription = this.product.description
        .replaceAll('<p', '<p class="text-body-xs text-body-s-desktop"')
        .replaceAll('<ul', '<ul class="text-body-xs text-body-s-desktop"')
        .replaceAll('<ol', '<ol class="text-body-xs text-body-s-desktop"')

      const deliveryReturns = this.$string('product.delivery_returns.content_html')
        .replaceAll('<p', '<p class="text-body-xs text-body-s-desktop"')
        .replaceAll('<ul', '<ul class="text-body-xs text-body-s-desktop"')
        .replaceAll('<ol', '<ol class="text-body-xs text-body-s-desktop"')

      return [
        {
          active: true,
          title: this.$string('product.description'),
          content: productDescription,
          classes: {
            title: 'text-body-xs text-body-s-desktop',
            content: 'product-description__description text-body-xs text-body-s-desktop rte-spacing-only',
          },
        },
        {
          title: this.$string('product.delivery_returns.title'),
          content: deliveryReturns,
          classes: {
            title: 'text-body-xs text-body-s-desktop',
            content: 'product-description__delivery-returns text-body-xs text-body-s-desktop rte-spacing-only',
          },
        },
      ]
    },

    /**
     * Compute support content.
     * @returns {String}
     */
    supportContent() {
      const string = this.$string(
        'product.support.content_html',
        { replace: { telephone: cnvs.store.shopPhone } },
      )

      return string
        .replaceAll('<p', '<p class="text-body-xs text-body-s-desktop"')
        .replaceAll('<a', '<a class="text-body-xs-link"')
    },
  },

  methods: {

    /**
     * Get USP Vue icon component.
     * @param {String} icon - Setting icon value.
     * @returns {String}
     */
    getUspIcon(icon) {
      return `icon-${icon.split('.')[1]}`
    },
  },
}
</script>

<style lang="scss">
@import './product-description';
</style>
